import { useMemo } from 'react';
import { Server, ShieldCheck, Network, Camera, BatteryCharging, Database, Globe, Layers, Cpu, MapPin, HardDrive, Wifi } from 'lucide-react';
import { KpiCard } from '../KpiCard';
import { DonutChart } from '../DonutChart';
import { CategoryRanking } from '../CategoryRanking';
import { SiteBars } from '../SiteBars';
import { WidgetCard, SectionTitle, Legend, EmptyMsg, useBarsIn, DashSkeleton, PALETTE } from '../primitives';

// SQL y cambios DNS son indicadores relacionados, no equipos adicionales.
const CATS = [
  { key: 'servers', name: 'Servidores', Icon: Server, color: '#00b8d4', link: '/assets/servers' },
  { key: 'firewalls', name: 'Firewalls', Icon: ShieldCheck, color: '#3b82f6', link: '/assets/firewalls' },
  { key: 'switches', name: 'Switches', Icon: Network, color: '#22c55e', link: '/assets/switches' },
  { key: 'cameras', name: 'Cámaras', Icon: Camera, color: '#f59e0b', link: '/assets/cameras' },
  { key: 'ups', name: 'UPS', Icon: BatteryCharging, color: '#8b5cf6', link: '/assets/ups' },
  { key: 'storage', name: 'Storage', Icon: HardDrive, color: '#ec4899', link: '/assets/nas' },
  { key: 'access_points', name: 'Access Points', Icon: Wifi, color: '#14b8a6', link: '/assets/access-points' },
];
const TYPE_COLORS = { VIRTUAL: '#00b8d4', CLOUD: '#8b5cf6', FISICO: '#3b82f6', 'FÍSICO': '#3b82f6', SERVIDOR: '#22c55e' };
const has = (stats, key, allowed) => !!stats && allowed[key] && Object.prototype.hasOwnProperty.call(stats, key);

export const InfraDashboard = ({ loading, stats, bySite, serversByType, allowed }) => {
  const barsIn = useBarsIn(loading);
  const ranking = useMemo(() => CATS.filter(c => has(stats, c.key, allowed))
    .map(c => ({ ...c, value: stats[c.key] }))
    .sort((a, b) => b.value - a.value), [stats, allowed]);
  const total = ranking.reduce((sum, category) => sum + category.value, 0);
  const servers = has(stats, 'servers', allowed) ? stats.servers : null;
  const typeData = useMemo(() => {
    const map = {};
    (serversByType || []).forEach(item => { map[item.name] = (map[item.name] || 0) + item.value; });
    return Object.entries(map).map(([name, value], i) => ({
      name, value, color: TYPE_COLORS[name.toUpperCase()] || PALETTE[(i + 3) % PALETTE.length],
    })).sort((a, b) => b.value - a.value);
  }, [serversByType]);
  const virtualCount = typeData.filter(item => item.name.toUpperCase() === 'VIRTUAL').reduce((sum, item) => sum + item.value, 0);
  const virtPct = servers > 0 ? (virtualCount / servers) * 100 : 0;
  const networkCategories = ranking.filter(item => ['firewalls', 'switches', 'cameras', 'ups'].includes(item.key));
  const networkTotal = networkCategories.reduce((sum, item) => sum + item.value, 0);
  const composition = ranking.filter(item => item.value > 0);

  if (loading) return <DashSkeleton />;

  return (
    <div className="space-y-6">
      {stats && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
        {ranking.length > 0 && <KpiCard testid="kpi-card-total-equipos" title="Total Equipos" value={total}
          subtitle="Categorías de infraestructura disponibles" Icon={Layers} to={allowed.inventory ? '/reports/inventory' : undefined} />}
        {servers !== null && <KpiCard testid="kpi-card-servidores" title="Servidores" value={servers}
          subtitle={`${total ? ((servers / total) * 100).toFixed(1) : 0}% de los equipos mostrados`} Icon={Server}
          iconClass="bg-blue-500/10 text-blue-500" to="/assets/servers" delay={80} />}
        {servers !== null && serversByType !== null && <KpiCard testid="kpi-card-virtualizacion" title="% Virtualización" value={virtPct} suffix="%" decimals={1}
          subtitle={`${virtualCount} de ${servers} servidores virtuales`} Icon={Cpu}
          iconClass="bg-teal-500/10 text-teal-500" to="/assets/servers" delay={160} />}
        {networkCategories.length > 0 && <KpiCard testid="kpi-card-red-perifericos" title="Red y periféricos" value={networkTotal}
          subtitle={networkCategories.map(item => item.name).join(', ')} Icon={Network}
          iconClass="bg-purple-500/10 text-purple-500" delay={240} />}
        {has(stats, 'sql_servers', allowed) && <KpiCard testid="kpi-card-sql" title="Instancias SQL Server" value={stats.sql_servers}
          subtitle="Instancias registradas no ignoradas" Icon={Database} iconClass="bg-pink-500/10 text-pink-500" to="/assets/sql" />}
        {has(stats, 'dns_critical', allowed) && <KpiCard testid="kpi-card-dns-critical" title="Cambios DNS críticos" value={stats.dns_critical}
          subtitle="Sin reconocer en los últimos 7 días" Icon={Globe} iconClass="bg-amber-500/10 text-amber-500" to="/reports/dns/traceability" />}
      </div>}

      {ranking.length > 0 && <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <WidgetCard testid="widget-inventory-composition" className="lg:col-span-5" delay={120}>
          <SectionTitle title="Distribución del Inventario" subtitle="Equipos de las categorías disponibles" />
          {composition.length ? <><DonutChart data={composition} centerValue={total} centerLabel="Equipos" /><Legend items={composition} /></> : <EmptyMsg text="Sin equipos registrados" />}
        </WidgetCard>
        <WidgetCard testid="widget-category-ranking" className="lg:col-span-7" delay={180}>
          <SectionTitle title="Desglose por Categoría" subtitle="Ranking de equipos por volumen" />
          <CategoryRanking items={ranking} total={total} barsIn={barsIn} />
        </WidgetCard>
      </div>}

      {(servers !== null && serversByType !== null || bySite !== null) && <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {servers !== null && serversByType !== null && <WidgetCard testid="widget-server-virtualization" className="lg:col-span-5" delay={220}>
          <SectionTitle title="Servidores: Tipo de Entorno" subtitle={`Desglose de ${servers} servidores`} />
          {typeData.length ? <div className="flex items-center gap-4">
            <div className="w-1/2"><DonutChart data={typeData} centerValue={servers} centerLabel="Servidores" innerRadius={46} outerRadius={70} height={180} /></div>
            <div className="w-1/2 space-y-2">{typeData.map(item => <div key={item.name} className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
              <span className="text-xs text-foreground flex-1 truncate capitalize">{item.name.toLowerCase()}</span>
              <span className="text-xs font-semibold font-mono">{item.value}</span>
              <span className="text-[11px] text-muted-foreground font-mono w-11 text-right">{servers ? ((item.value / servers) * 100).toFixed(1) : 0}%</span>
            </div>)}</div>
          </div> : <EmptyMsg />}
        </WidgetCard>}
        {bySite !== null && <WidgetCard testid="widget-site-distribution" className={servers !== null && serversByType !== null ? 'lg:col-span-7' : 'lg:col-span-12'} delay={280}>
          <SectionTitle title="Equipos por Site" subtitle={`Categorías disponibles · Top ${Math.min(bySite.length, 8)}`} Icon={MapPin} />
          {bySite.length ? <SiteBars data={bySite} barsIn={barsIn} /> : <EmptyMsg />}
        </WidgetCard>}
      </div>}
    </div>
  );
};
